import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Palette, LogOut, ChevronRight, Moon, Sun } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import Navigation from '../components/Navigation';
import MimoCharacter from '../components/MimoCharacter';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const ageModes = [
  { id: 'explorador', label: 'Explorador', age: '5-10', color: '#FFD166' },
  { id: 'companero', label: 'Compañero', age: '11-17', color: '#4F8CFF' },
  { id: 'aliado', label: 'Aliado', age: '18+', color: '#06D6A0' }
];

export const Settings = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout, getAgeConfig } = useUser();
  const { theme, toggleTheme } = useTheme();
  const config = getAgeConfig();
  
  const [isAgeModeOpen, setIsAgeModeOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleAgeModeChange = async (mode) => {
    try {
      await updateUser({ age_mode: mode });
      setIsAgeModeOpen(false);
    } catch (error) {
      console.error('Error updating age mode:', error);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const currentMode = ageModes.find(m => m.id === user?.age_mode) || ageModes[2];

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8 lg:pl-20">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className={`${config.fontClass} text-2xl sm:text-3xl font-bold text-foreground`}>
            Ajustes
          </h1>
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className={`glass ${config.cardRadius}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-foreground text-lg">{user?.name}</h2>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <div className="w-12 h-12">
                  <MimoCharacter mood="happy" size="sm" showAura={false} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Settings List */}
        <div className="space-y-4">
          {/* Theme Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className={`glass ${config.cardRadius}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? (
                      <Moon className="w-5 h-5 text-[#4F8CFF]" />
                    ) : (
                      <Sun className="w-5 h-5 text-[#FFD166]" />
                    )}
                    <div>
                      <p className="font-medium text-foreground">Tema Oscuro</p>
                      <p className="text-sm text-muted-foreground">
                        {theme === 'dark' ? 'Activado' : 'Desactivado'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={theme === 'dark'}
                    onCheckedChange={toggleTheme}
                    data-testid="theme-switch"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Age Mode */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Dialog open={isAgeModeOpen} onOpenChange={setIsAgeModeOpen}>
              <DialogTrigger asChild>
                <Card className={`glass ${config.cardRadius} cursor-pointer card-hover`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Palette className="w-5 h-5" style={{ color: currentMode.color }} />
                        <div>
                          <p className="font-medium text-foreground">Modo de MIMO</p>
                          <p className="text-sm text-muted-foreground">
                            {currentMode.label} ({currentMode.age} años)
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cambiar Modo de MIMO</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-4">
                  {ageModes.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => handleAgeModeChange(mode.id)}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        user?.age_mode === mode.id
                          ? 'ring-2'
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      style={{
                        ringColor: user?.age_mode === mode.id ? mode.color : undefined,
                        backgroundColor: user?.age_mode === mode.id ? `${mode.color}10` : undefined
                      }}
                      data-testid={`settings-mode-${mode.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: mode.color }}
                        />
                        <span className="font-medium text-foreground">{mode.label}</span>
                        <span className="text-sm text-muted-foreground">({mode.age} años)</span>
                      </div>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>

          {/* Hobbies info */}
          {user?.hobbies && user.hobbies.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className={`glass ${config.cardRadius}`}>
                <CardContent className="p-4">
                  <p className="font-medium text-foreground mb-2">Tus intereses</p>
                  <div className="flex flex-wrap gap-2">
                    {user.hobbies.map((hobby) => (
                      <span
                        key={hobby}
                        className="px-3 py-1 rounded-full text-sm bg-[#FF7A59]/10 text-[#FF7A59]"
                      >
                        {hobby}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Logout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="pt-4"
          >
            <Button
              onClick={handleLogout}
              disabled={isLoggingOut}
              variant="outline"
              className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar Sesión'}
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
